import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Activity, TrendingDown, AlertCircle, Timer, MessageSquare, HeartPulse } from "lucide-react";

const Tile = ({ icon: Icon, value, label, bg, iconColor, testId }) => (
  <div className={`rounded-2xl p-5 ${bg}`} data-testid={testId}>
    <Icon className="h-6 w-6" style={{ color: iconColor }} />
    <p className="mt-3 text-3xl font-bold text-[#1f3a5f]">{value}</p>
    <p className="text-sm text-[#1f3a5f]/70">{label}</p>
  </div>
);

export default function PhysioDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [assist, setAssist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [d, a] = await Promise.allSettled([api.physioDashboard(), api.physioAssistance()]);
      if (d.status === "fulfilled") setData(d.value); else throw d.reason;
      if (a.status === "fulfilled") setAssist(a.value);
    }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const d = data || {};
  const num = (v) => (v ?? 0);
  const executed = assist?.executed_minutes ?? 0;
  const prescribed = assist?.prescribed_minutes ?? 0;
  const pct = prescribed > 0 ? Math.round((executed / prescribed) * 100) : 0;
  const firstName = user?.full_name || user?.username;

  return (
    <div>
      <PageHeader title={`Buongiorno, ${firstName}`} subtitle="Aderenza, minuti eseguiti e alert clinici sempre sotto controllo." />

      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Panoramica clinica</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={Users} value={num(d.total_patients)} label="Pazienti totali" bg="bg-[#e6f0e9]" iconColor="#5f9c96" testId="stat-patients" />
        <Tile icon={Activity} value={num(d.active_patients)} label="Pazienti attivi (7gg)" bg="bg-[#fbe9db]" iconColor="#5f9c96" testId="stat-active" />
        <Tile icon={TrendingDown} value={num(d.low_adherence)} label="Bassa aderenza" bg="bg-[#fbe9db]" iconColor="#e0a06a" testId="stat-adherence" />
        <Tile icon={AlertCircle} value={num(d.pain_alerts)} label="Alert dolore" bg="bg-[#f8dede]" iconColor="#d98a8a" testId="stat-pain" />
      </div>

      <p className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Minuti assistenziali (questo mese)</p>
      <Card className="border-border/70">
        <CardContent className="flex flex-wrap items-center gap-6 p-6" data-testid="assistance-summary">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#e6f0e9]"><Timer className="h-6 w-6 text-[#5f9c96]" /></div>
          <div><p className="text-2xl font-bold text-[#1f3a5f]">{executed}′</p><p className="text-sm text-muted-foreground">Eseguiti</p></div>
          <div className="h-10 w-px bg-border" />
          <div><p className="text-2xl font-bold text-[#1f3a5f]">{prescribed}′</p><p className="text-sm text-muted-foreground">Prescritti</p></div>
          <div className="h-10 w-px bg-border" />
          <div><p className="text-2xl font-bold text-[#5f9c96]">{pct}%</p><p className="text-sm text-muted-foreground">Aderenza</p></div>
        </CardContent>
      </Card>

      <Card className="mt-4 border-border/70">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10"><MessageSquare className="h-5 w-5 text-primary" /></div>
          <div>
            <p className="text-lg font-bold text-[#1f3a5f]">{num(d.unread_messages)} messaggi non letti</p>
            <p className="text-sm text-muted-foreground">Vai alla sezione Chat per rispondere ai tuoi pazienti.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4 border-none bg-[#eaf3ee]">
        <CardContent className="flex items-start gap-4 p-6">
          <HeartPulse className="mt-0.5 h-5 w-5 text-[#5f9c96]" />
          <div>
            <p className="font-bold text-[#1f3a5f]">Riabilitazione consapevole</p>
            <p className="text-sm text-[#1f3a5f]/70">Ricorda: l'app è uno strumento di supporto. Le valutazioni cliniche restano di tua competenza professionale.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
